/**
 * screens/admin/adminCustomerActivity.tsx — N-6 · Customer activity across tenants.
 *
 * INTERNAL CONSOLE ONLY. SKPLATFORM-UICC-006 §8 / DB-006 S7b.
 *
 * This is the surface for SANITY-CHECKING THE METER BEFORE A PRICE IS
 * PUBLISHED. Instrument in P1, price in P2 — so there are real numbers to look
 * at before anything goes public.
 *
 * ── WHY BOTH COMPONENT FIGURES ARE SHOWN ──────────────────────────────────
 *
 * The authenticated and transacted halves are shown ALONGSIDE the
 * de-duplicated total, because they will NOT sum to it and somebody will ask
 * why. The total is a union: a customer who both signed in and transacted is
 * counted once. Showing only the total invites the question; showing all three
 * answers it on the screen.
 *
 * Sortable by blocks over, because that is the column that decides whether a
 * price is plausible.
 *
 * No price, no currency — even here. Platform counts; SK Meter prices; SK
 * Billing invoices. This screen is the input to that decision, not the output.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { SettingsHeader, Panel, DataTable, Tag, type Column } from '../../components/platform/primitives';
import { PEmpty } from '../../components/platform/controls';
import { FS, FW } from '../../utils/platformTokens';
import { listCustomerActivityAcrossTenants } from '../../services/platform/platformApi';
import type { CustomerActivityPeriodRow } from '../../schema/platform/types';

type SortKey = 'blocks' | 'active' | 'org';

const AdminCustomerActivity: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [rows, setRows] = useState<CustomerActivityPeriodRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortKey>('blocks');

  useEffect(() => {
    void (async () => {
      try { setRows(await listCustomerActivityAcrossTenants().catch(() => [])); }
      finally { setLoading(false); }
    })();
  }, []);

  const ordered = useMemo(() => {
    const copy = [...rows];
    if (sort === 'org') return copy.sort((a, b) => (a.org_name ?? a.platform_org_id).localeCompare(b.org_name ?? b.platform_org_id));
    if (sort === 'active') return copy.sort((a, b) => b.active_count - a.active_count);
    return copy.sort((a, b) => b.blocks_over_allowance - a.blocks_over_allowance);
  }, [rows, sort]);

  const num = (n: number, tone?: string) => (
    <Text style={{ fontSize: FS.sm, color: tone ?? c.ink, fontFamily: theme.font.mono }}>
      {n.toLocaleString()}
    </Text>
  );

  const columns: Column<CustomerActivityPeriodRow>[] = [
    {
      key: 'org',
      header: 'Organisation',
      render: (r) => (
        <View>
          <Text style={{ fontSize: FS.sm, color: c.ink }}>{r.org_name ?? '—'}</Text>
          <Text selectable style={{ fontSize: FS.xs, color: c.ink4, fontFamily: theme.font.mono }}>
            {r.platform_org_id}
          </Text>
        </View>
      ),
    },
    {
      key: 'zone',
      header: 'Zone',
      width: 90,
      render: (r) => (
        <Text style={{ fontSize: FS.sm, color: c.ink2, fontFamily: theme.font.mono }}>
          {r.zone ?? '—'}
        </Text>
      ),
    },
    {
      key: 'active',
      header: 'Active customers',
      width: 150,
      align: 'right',
      render: (r) => num(r.active_count),
    },
    {
      /* The two component figures. They will not sum to the total — that is
         the point of showing them. */
      key: 'authenticated',
      header: 'Authenticated',
      width: 140,
      align: 'right',
      render: (r) => num(r.authenticated_count, c.ink3),
    },
    {
      key: 'transacted',
      header: 'Transacted',
      width: 130,
      align: 'right',
      render: (r) => num(r.transacted_count, c.ink3),
    },
    {
      key: 'allowance',
      header: 'Included',
      width: 120,
      align: 'right',
      render: (r) => num(r.allowance_at_period, c.ink3),
    },
    {
      key: 'blocks',
      header: 'Blocks over',
      width: 130,
      align: 'right',
      render: (r) =>
        r.blocks_over_allowance > 0
          ? <Tag tone="warn">{r.blocks_over_allowance.toLocaleString()}</Tag>
          : num(0, c.ink4),
    },
  ];

  const SortTab: React.FC<{ k: SortKey; label: string }> = ({ k, label }) => (
    <Pressable onPress={() => setSort(k)}>
      <Text
        style={{
          fontSize: FS.sm,
          color: sort === k ? c.accent : c.ink3,
          fontWeight: sort === k ? FW.medium : FW.regular,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1440, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Customer activity"
        sub="Active customers per tenant, per period. The pre-pricing sanity check — Softknack internal."
      />

      <View style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: FS.sm, color: c.ink2, lineHeight: 20, maxWidth: 820 }}>
          Active means a customer who signed in or transacted during the period. The total is a
          de-duplicated union, so the authenticated and transacted columns will not add up to it —
          a customer who did both is counted once.
        </Text>
      </View>

      <Panel>
        <View style={{ flexDirection: 'row', gap: 18, marginBottom: 12, alignItems: 'center' }}>
          <Text style={{ fontSize: FS.xs, color: c.ink4 }}>Sort by</Text>
          <SortTab k="blocks" label="Blocks over" />
          <SortTab k="active" label="Active customers" />
          <SortTab k="org" label="Organisation" />
        </View>

        <DataTable
          columns={columns}
          rows={ordered}
          keyExtractor={(r) => `${r.platform_org_id}:${r.period_start}`}
          minWidth={1180}
          empty={
            <PEmpty
              title={loading ? 'Loading…' : 'No activity periods computed yet'}
              children={
                <Text style={{ fontSize: FS.sm, color: c.ink3, textAlign: 'center', maxWidth: 560, lineHeight: 19 }}>
                  Figures appear once the identity service and the verticals begin contributing
                  their halves. Counting is instrumented before any price is published, so there
                  are real numbers to look at first.
                </Text>
              }
            />
          }
        />
      </Panel>
    </ScrollView>
  );
};

export default AdminCustomerActivity;
